import { useEffect } from 'react';

import {
  SIGNUP_ABANDON_ON_LOHP_TIMESTAMP_KEY,
  clearSignupAbandonOnLOHPTimestamp,
  getPreviouslyLoggedInTimestamp,
  getSignupAbandonOnLOHPTimestamp,
} from 'bundles/authentication-common/utils/coachStorageUtils';
import {
  LOHP_VIEW_TIMESTAMP_KEY,
  SHOW_AI_ASSISTANT_TIMESTAMP_KEY,
  clearActiveExperiment,
  clearLOHPViewTimestamp,
  clearShowAiAssistantTimestamp,
  getLOHPViewTimestamp,
  getShowAiAssistantTimestamp,
} from 'bundles/front-page/utils/coachStorageUtils';

const ALL_COACH_TIMESTAMP_STORAGE_KEYS = [
  LOHP_VIEW_TIMESTAMP_KEY,
  SHOW_AI_ASSISTANT_TIMESTAMP_KEY,
  SIGNUP_ABANDON_ON_LOHP_TIMESTAMP_KEY,
];

const clearCoachStorage = (coachStorageKey: (typeof ALL_COACH_TIMESTAMP_STORAGE_KEYS)[number]) => {
  switch (coachStorageKey) {
    case LOHP_VIEW_TIMESTAMP_KEY:
      clearLOHPViewTimestamp();
      break;
    case SHOW_AI_ASSISTANT_TIMESTAMP_KEY:
      clearShowAiAssistantTimestamp();
      break;
    case SIGNUP_ABANDON_ON_LOHP_TIMESTAMP_KEY:
      clearSignupAbandonOnLOHPTimestamp();
      break;
    default:
      break;
  }
};

const getCoachStorageTimestamp = (coachStorageKey: (typeof ALL_COACH_TIMESTAMP_STORAGE_KEYS)[number]) => {
  switch (coachStorageKey) {
    case LOHP_VIEW_TIMESTAMP_KEY:
      return getLOHPViewTimestamp();
    case SHOW_AI_ASSISTANT_TIMESTAMP_KEY:
      return getShowAiAssistantTimestamp();
    case SIGNUP_ABANDON_ON_LOHP_TIMESTAMP_KEY:
      return getSignupAbandonOnLOHPTimestamp();
    default:
      return null;
  }
};

/**
 * Goes through all of the timestamp coach storage keys and clears the coach storage if the timestamp is older than the previously logged in timestamp
 * Also clears the active experiment
 */
export const clearAllCoachStorageBeforeLogin = () => {
  const previouslyLoggedInTimestamp = getPreviouslyLoggedInTimestamp();

  // If there is no previously logged in timestamp, then we don't need to clear anything
  if (!previouslyLoggedInTimestamp) {
    return;
  }

  let isAnyTimestampOlderThanPreviouslyLoggedInTimestamp = false;

  ALL_COACH_TIMESTAMP_STORAGE_KEYS.forEach((coachStorageKey) => {
    const coachStorageTimestamp = getCoachStorageTimestamp(coachStorageKey);
    if (coachStorageTimestamp && coachStorageTimestamp < previouslyLoggedInTimestamp) {
      isAnyTimestampOlderThanPreviouslyLoggedInTimestamp = true;
      clearCoachStorage(coachStorageKey);
    }
  });

  if (isAnyTimestampOlderThanPreviouslyLoggedInTimestamp) {
    clearActiveExperiment();
  }
};

/**
 * This hook is used the clean up the coach storage when the user has logged in/signed up after the coach timestamps.
 */
export const useLOHPCoachStorageCleaner = () => {
  useEffect(() => {
    clearAllCoachStorageBeforeLogin();
  }, []);
};
